// D-191 — uncaught errors, into the Console.
//
// This is the half of the log the bus cannot supply. A `TypeError` thrown out of a built-in's
// render, or a rejected promise nobody caught, produces no kernel event at all — it goes to the
// browser console and, on a deployed desktop, to nobody. That is the single most useful line a
// user could hand back, so the shell listens for it.
//
// It lives in `host/` because it touches `window`, which the kernel does not (§6.5), and because
// installing global listeners is a composition act: exactly one of these may exist per page.
import type { HangarKernel } from '@/kernel';

/**
 * How much of a stack is kept.
 *
 * The top frames are the ones that name the failure; the rest is React's internals repeated for
 * every component in the tree, and it would fill the ring with one entry.
 */
const STACK_CHARS = 400;

function describe(reason: unknown): { message: string; detail?: string } {
  if (reason instanceof Error) {
    const stack = reason.stack?.slice(0, STACK_CHARS);
    return {
      message: `${reason.name}: ${reason.message}`,
      ...(stack === undefined ? {} : { detail: stack }),
    };
  }
  if (typeof reason === 'string') return { message: reason };
  try {
    return { message: JSON.stringify(reason) ?? String(reason) };
  } catch {
    // A rejection value with a circular reference or a throwing getter. The point of this
    // handler is to survive whatever it is handed, so it says what it can and moves on.
    return { message: String(reason) };
  }
}

/**
 * Record uncaught errors and unhandled rejections. Returns the teardown.
 *
 * Neither listener calls `preventDefault`: the browser's own console keeps its output and any
 * other reporter keeps its event. The shell is an *additional* observer here, never the one
 * that swallows the error — a log viewer that made errors stop appearing in DevTools would be
 * actively worse than no log viewer.
 */
export function installErrorLog(kernel: HangarKernel): () => void {
  if (typeof window === 'undefined') return () => {};

  const onError = (event: ErrorEvent): void => {
    const described = describe(event.error ?? event.message);
    const where = event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined;
    kernel.logs.log({
      source: 'shell',
      level: 'error',
      message: described.message,
      ...(described.detail ?? where ? { detail: described.detail ?? where ?? '' } : {}),
    });
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    const described = describe(event.reason);
    kernel.logs.log({
      source: 'shell',
      level: 'error',
      message: `Unhandled rejection — ${described.message}`,
      ...(described.detail === undefined ? {} : { detail: described.detail }),
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
